import { useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  GoogleAuthProvider, 
  signInWithPopup,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { auth } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Mail, Lock, User } from 'lucide-react';
import styles from '../styles/LoginModal.module.css';

interface LoginModalProps {
  open: boolean;
  onClose: () => void;
}

export function LoginModal({ open, onClose }: LoginModalProps) {
  const [mode, setMode] = useState<'login' | 'signup' | 'not-verified' | 'forgot-password'>('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const authContext = useAuth();

  // Check if email domain is allowed
  const isEmailDomainAllowed = (email: string): boolean => {
    const allowedDomains = ['@dashverse.ai', '@dashtoon.com','@dashversemail.com', '@dashverseemail.com'];
    return allowedDomains.some(domain => email.toLowerCase().endsWith(domain));
  };

  // Studio-style verification check
  useEffect(() => {
    if (authContext.user && authContext.user.email && !authContext.user.isAnonymous) {
      if (authContext.isVerified) {
        onClose();
      } else if (mode !== 'not-verified') {
        setMode('not-verified');
      }
    }
  }, [authContext.user, authContext.isVerified, mode, onClose]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(''); // Clear error on input
  };


  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Check if the Google account email domain is allowed
      if (result.user?.email && !isEmailDomainAllowed(result.user.email)) {
        // Sign out the user since they're not allowed
        await result.user.delete();
        window.location.href = '/restricted-access';
        return;
      }

      if (result.user?.emailVerified) {
        toast.success('Successfully signed in with Google!');
        onClose();
      } else {
        setMode('not-verified');
      }
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      setError(error.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setError('Please enter your email');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      toast.success('Password reset email sent!');
      setMode('login');
      setResetEmail('');
      setError('');
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError('Failed to send password reset email');
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setFormData({ email: '', password: '', name: '' });
    setError('');
  };

  const renderContent = () => {
    // Email verification screen (like studio project)
    if (mode === 'not-verified') {
      return (
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold">Email Verification Required</h3>
          <p className="text-sm text-muted-foreground">
            We've sent a verification email to {authContext.user?.email}. 
            Please check your inbox and click the verification link to continue.
          </p>
          <Button 
            onClick={() => authContext.sendVerificationEmail()}
            variant="outline"
            disabled={loading}
          >
            Resend Verification Email
          </Button>
        </div>
      );
    }

    // Forgot password screen (like studio project)
    if (mode === 'forgot-password') {
      return (
        <form onSubmit={handleForgotPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="resetEmail">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="resetEmail"
                type="email"
                placeholder="Enter your email"
                className="pl-9"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
            </div>
          </div>
          
          {error && <div className="text-sm text-red-600">{error}</div>}
          
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Sending...' : 'Send Reset Email'}
          </Button>
          
          <Button 
            type="button" 
            variant="ghost" 
            className="w-full"
            onClick={() => {
              setMode('login');
              setError('');
              setResetEmail('');
            }}
          >
            Return to Login
          </Button>
        </form>
      );
    }

    // Main login/signup forms
    return (
      <>
        <Button
          onClick={handleGoogleSignIn}
          variant="outline"
          className="w-full mb-4"
          disabled={loading}
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Sign in with Google
        </Button>

        <div className="text-xs text-muted-foreground text-center mt-4">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </div>
      </>
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-background z-50 animate-in fade-in-0 duration-300">
      <div className="grid lg:grid-cols-2 h-full">

        {/* Left Panel - Dark Section */}
        <div className="hidden lg:block relative overflow-hidden">
          {/* Company Logo */}
          <div className="absolute top-0 left-0 right-0 p-8 z-10">
            <span className="text-lg font-medium text-white">Dashreels CMS</span>
          </div>

          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=1200&q=80" 
              alt="Creative workspace" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-black/30" />
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
            <div className="max-w-md">
              <blockquote className="space-y-2">
                <p className="text-lg font-medium">
                  "This platform aims to ease your content management and delivery process"
                </p>
                <footer className="text-sm text-white/70">
                  Dashverse Team
                </footer>
              </blockquote>
            </div>
          </div>
        </div>
        
        {/* Right Panel - Auth Form */}
        <div className="flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-sm space-y-6">
            <span className="lg:hidden block text-lg font-medium mb-8">Dashreels CMS</span>
            
            <div className="space-y-3 text-center mb-8">
              <h1 className="text-2xl font-semibold tracking-tight">
                {mode === 'not-verified' 
                  ? 'Email Verification' 
                  : mode === 'forgot-password'
                  ? 'Reset Password'
                  : mode === 'signup'
                  ? 'Create your account'
                  : 'Welcome back'
                }
              </h1>
              <p className="text-sm text-muted-foreground">
                {mode === 'not-verified' 
                  ? 'Please verify your email to continue'
                  : mode === 'forgot-password'
                  ? 'Enter your email to reset your password'
                  : mode === 'signup'
                  ? 'Sign up to get started with Dashreels CMS'
                  : 'Enter your credentials to sign in to your account'
                }
              </p>
            </div>
            
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}